import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { ActivityFilters } from '../../api/types';

const ISSUE_DEBOUNCE_MS = 400;
const LOCAL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseLocalDate(value: string): Date | undefined {
  const match = LOCAL_DATE_PATTERN.exec(value);
  if (!match) return undefined;
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (
    date.getFullYear() !== Number(year) ||
    date.getMonth() !== Number(month) - 1 ||
    date.getDate() !== Number(day)
  ) {
    return undefined;
  }
  return date;
}

export type ActivityDateRangeResult =
  | {
      valid: true;
      createdFrom?: string;
      createdToExclusive?: string;
    }
  | { valid: false; error: string };

export function buildActivityDateRange(
  fromInput: string,
  toInput: string,
): ActivityDateRangeResult {
  const from = fromInput ? parseLocalDate(fromInput) : undefined;
  const to = toInput ? parseLocalDate(toInput) : undefined;
  if ((fromInput && !from) || (toInput && !to)) {
    return { valid: false, error: 'Informe datas válidas.' };
  }
  if (isReversedRange(from, to)) {
    return { valid: false, error: 'A data inicial deve ser anterior à final.' };
  }
  const nextDay = to
    ? new Date(to.getFullYear(), to.getMonth(), to.getDate() + 1)
    : undefined;
  return {
    valid: true,
    createdFrom: from?.toISOString(),
    createdToExclusive: nextDay?.toISOString(),
  };
}

function isReversedRange(from?: Date, to?: Date): boolean {
  return Boolean(from && to && from.getTime() > to.getTime());
}

function formatLocalDateInput(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isoToLocalDateInput(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? formatLocalDateInput(date) : '';
}

function exclusiveIsoToLocalDateInput(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  date.setDate(date.getDate() - 1);
  return formatLocalDateInput(date);
}

export function useIssueDraft(
  issueExternalId: string | undefined,
  onFiltersChange: (patch: Partial<ActivityFilters>) => void,
) {
  const [input, setInput] = useState(issueExternalId ?? '');
  useEffect(() => setInput(issueExternalId ?? ''), [issueExternalId]);
  useDebounce(
    () => {
      const normalized = input.trim() || undefined;
      if (normalized !== issueExternalId) {
        onFiltersChange({ issueExternalId: normalized });
      }
    },
    ISSUE_DEBOUNCE_MS,
    [input, issueExternalId, onFiltersChange],
  );
  return [input, setInput] as const;
}

export function useDateDraft(
  filters: ActivityFilters,
  onFiltersChange: (patch: Partial<ActivityFilters>) => void,
) {
  const [fromInput, setFromInput] = useState(() =>
    isoToLocalDateInput(filters.createdFrom),
  );
  const [toInput, setToInput] = useState(() =>
    exclusiveIsoToLocalDateInput(filters.createdToExclusive),
  );
  const range = useMemo(
    () => buildActivityDateRange(fromInput, toInput),
    [fromInput, toInput],
  );
  useEffect(
    () => setFromInput(isoToLocalDateInput(filters.createdFrom)),
    [filters.createdFrom],
  );
  useEffect(
    () => setToInput(exclusiveIsoToLocalDateInput(filters.createdToExclusive)),
    [filters.createdToExclusive],
  );
  const updateFromInput = (value: string) => {
    setFromInput(value);
    const nextRange = buildActivityDateRange(value, toInput);
    if (nextRange.valid) onFiltersChange(nextRange);
  };
  const updateToInput = (value: string) => {
    setToInput(value);
    const nextRange = buildActivityDateRange(fromInput, value);
    if (nextRange.valid) onFiltersChange(nextRange);
  };
  return {
    fromInput,
    setFromInput: updateFromInput,
    toInput,
    setToInput: updateToInput,
    range,
  };
}

export type ActivityDateDraft = ReturnType<typeof useDateDraft>;
